import { compressToBase64, decompressFromBase64 } from "lz-string";
import * as z from "zod/v4";
import { ArrayStringFilterDataSchema } from "../../views/advanced-filters/array-string-filter";
import { BooleanFilterDataSchema } from "../../views/advanced-filters/boolean-filter";
import { DateFilterDataSchema } from "../../views/advanced-filters/date-filter";
import { AdvancedFilterType } from "../../views/advanced-filters/generic-advanced-filter-data";
import { NumberFilterDataSchema } from "../../views/advanced-filters/number-filter";
import type { CustomAnalyticsReportColumn } from "./custom-analytics-report";

export const CUSTOM_ANALYTICS_REPORT_FILTERS_MAX_COUNT = 20;

export const CustomAnalyticsReportFilterSchema = z.discriminatedUnion("type", [
  ArrayStringFilterDataSchema,
  BooleanFilterDataSchema,
  DateFilterDataSchema,
  NumberFilterDataSchema,
]);

export const CustomAnalyticsReportFiltersSchema = z
  .array(CustomAnalyticsReportFilterSchema)
  .max(CUSTOM_ANALYTICS_REPORT_FILTERS_MAX_COUNT)
  .default([]);

export type CustomAnalyticsReportFilter = z.infer<
  typeof CustomAnalyticsReportFilterSchema
>;

export function customAnalyticsReportFiltersToQueryString(
  filters: readonly CustomAnalyticsReportFilter[],
): string {
  return compressToBase64(JSON.stringify(filters));
}

/** @throws If the string does not represent a valid custom report filter array */
export function queryStringToCustomAnalyticsReportFilters(
  queryString: string,
): CustomAnalyticsReportFilter[] {
  return CustomAnalyticsReportFiltersSchema.parse(
    JSON.parse(decompressFromBase64(queryString)),
  );
}

export function getCustomAnalyticsReportColumnFilterType(
  column: CustomAnalyticsReportColumn,
): AdvancedFilterType | null {
  switch (column.kind) {
    case "text":
      return AdvancedFilterType.ARRAY_TO_STRING;
    case "number":
    case "integer":
    case "currency":
    case "percent":
      return AdvancedFilterType.NUMBER;
    case "date":
    case "datetime":
      return AdvancedFilterType.DATE;
    case "boolean":
      return AdvancedFilterType.BOOLEAN;
    case "json":
      return null;
  }
}

export function isCustomAnalyticsReportColumnFilterable(
  column: CustomAnalyticsReportColumn,
): boolean {
  return getCustomAnalyticsReportColumnFilterType(column) !== null;
}
